import { TableRow } from "@/components/ui/table";
import { TableCell } from "@/components/ui/table";
import { DateUtils, getWeekdayLabel, type Weekday } from "@/utils/dayUtill";
import { Input } from "@/components/ui/input";
import { PencilLine, Trash2, GripVertical } from "lucide-react";
import type {
  NurseResponse,
  NurseWithRelation,
  UnionShift,
} from "@/types/nurse-type";
import { OPTIONS as SHIFT_OPTIONS } from "@constants/SHIFT";
import {
  Controller,
  useForm,
  FormProvider,
  type FieldErrors,
} from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QUERY_BASE_KEY } from "@/constants/query-key";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { sortShiftArray } from "@/utils/nurse-util";
import { resolveGradeLabel } from "@/utils/grade-util";
import { useIsAdmin, useIsGroupNurse } from "@/hooks/useAuth";
import { toast } from "@/components/toast/CustomToast";
import StyledDateInput from "@/components/common/StyledDateInput";
import MemoCell from "@/components/feature/head_nurse_mgmt/MemoCell";
import { ShiftMapper } from "@/utils/shift-util";
import ShiftIcon from "@/components/ui/shift-icon";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import useGradeConfig from "@/hooks/useGradeConfig";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { ShiftPopover } from "./ShiftPopover";
import { NurseBulkSchema } from "../schema/nurseBulkSchema";
import useNurseMutation from "../hooks/useNurseMutation";
import { cn } from "@/utils/cn";
import dayjs from "dayjs";
import httpClient from "@/config/axios";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { useGroupSelection } from "@/hooks/useGroupSelection";
import { images } from "@/assets/img/imagesMap";
import CustomModal from "@/components/modal/CustomModal";
import useRosterConfig from "@/hooks/useRosterConfig";
import { getDefaultShiftsSortedBySequence } from "@/utils/shift-util";

type NurseBulkFormValues = z.infer<typeof NurseBulkSchema>;
export function NurseTableRow({
  nurse,
  idx,
  selectedGroupId,
  activeForSideProfile,
  selectIdForSideprofile,
  enableDragHandle = false,
  disableEdit = false,
  onCancelNewNurse,
  hideEditColumn = false,
  visibleColumns,
  enableSelection = false,
  isSelected = false,
  onToggleSelection,
}: {
  nurse: NurseWithRelation;
  idx: number;
  selectedGroupId: string | null;
  activeForSideProfile: boolean;
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  enableDragHandle?: boolean;
  disableEdit?: boolean;
  onCancelNewNurse?: () => void;
  hideEditColumn?: boolean;
  visibleColumns?: string[];

  enableSelection?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (nurseId: string) => void;
}) {
  const formattedBirthDate = nurse.birth_date
    ? dayjs(nurse.birth_date).format("YYYY-MM-DD")
    : "-";

  const [isEditMode, setIsEditMode] = useState(
    nurse.nurse_id.startsWith("temp_")
  );

  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isNightWeekendBlockedModalOpen, setIsNightWeekendBlockeModalOpen] =
    useState<boolean>(false);

  // 근무자 삭제 모달에서 병동명을 보여주기 위함.
  const { groups } = useGroupSelection();
  const groupName = groups?.find(
    (g) => g.group_id === selectedGroupId
  )?.group_name;

  const isAdmin = useIsAdmin();
  const isGroupNurse = useIsGroupNurse();

  const groupId = isAdmin ? selectedGroupId : "";

  const queryClient = useQueryClient();
  const get = queryClient.getQueryData<NurseResponse[]>([
    QUERY_BASE_KEY.NURSES,
    groupId,
  ]);

  const { data: gradeData } = useGradeConfig({
    selectedGroupId: selectedGroupId!,
  });

  // 개인 프리셉터 관련 데이터
  const rowRef = useRef<HTMLTableRowElement>(null);

  function filterDate(date: Date | null | string) {
    if (!date) return "";
    return DateUtils.parseKoreanDate(date).format("YYYY-MM-DD");
  }

  const { data: shiftsRes } = useShiftsQuery(
    isAdmin ? (selectedGroupId ?? "") : ""
  );
  const { data: serverConfig } = useRosterConfig();
  const isUseMid = serverConfig?.use_mid;

  // Shift 인스턴스 생성
  const shiftMapper = useMemo(() => {
    if (!shiftsRes) return null;
    return new ShiftMapper(shiftsRes);
  }, [shiftsRes]);

  // 근무형태 옵션: default_shift 기준 (오프/서브코드 제외)
  const shiftOptions = useMemo<Array<{ label: string; value: string }>>(() => {
    if (!shiftsRes || shiftsRes.length === 0) {
      const base = SHIFT_OPTIONS.slice(1).map((o) => ({
        label: (o as { short: string }).short,
        value: (o as { short: string }).short,
      }));
      return isUseMid !== false ? [...base, { label: "M", value: "M" }] : base;
    }

    const deduped = new Map<string, { label: string; value: string }>();
    getDefaultShiftsSortedBySequence(shiftsRes, { excludeOff: true }).forEach(
      (shift) => {
        const code = shift.default_shift;
        if (!code) return;
        if (isUseMid === false && code === "M") return;
        if (!deduped.has(code)) {
          // 값은 default_shift(저장용), 라벨은 shift_id(표시용)
          deduped.set(code, { label: shift.shift_id || code, value: code });
        }
      }
    );

    return Array.from(deduped.values());
  }, [shiftsRes, isUseMid]);

  // 고정근무 옵션 (shift_gb === "고정"인 것들). use_mid가 false면 M 제외
  const fixedShiftOptions = useMemo<
    Array<{ label: string; value: string }>
  >(() => {
    if (!shiftsRes) return [];
    const list = shiftsRes
      .filter((shift: any) => shift.shift_gb === "고정")
      .map((shift: any) => ({
        label: shift.name,
        value: shift.shift_id,
        color: shift.color,
      }));
    if (isUseMid === false) {
      return list.filter((o: { value: string }) => o.value !== "M");
    }
    return list;
  }, [shiftsRes, isUseMid]);

  const defaultShiftColorMap = useMemo(() => {
    const map = new Map<string, string>();
    (shiftsRes ?? []).forEach((shift: any) => {
      if (shift.default_shift && !map.has(shift.default_shift)) {
        map.set(shift.default_shift, shift.color);
      }
    });
    return map;
  }, [shiftsRes]);

  const defaultShiftIdMap = useMemo(() => {
    const map = new Map<string, string>();
    (shiftsRes ?? []).forEach((shift: any) => {
      if (shift.default_shift && !map.has(shift.default_shift)) {
        map.set(shift.default_shift, shift.shift_id || shift.default_shift);
      }
    });
    return map;
  }, [shiftsRes]);

  const { preceptor, preceptees, ...pureNurse } = nurse;
  void preceptor;
  void preceptees;

  const createUtcDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  };

  const methods = useForm<NurseBulkFormValues>({
    resolver: zodResolver(NurseBulkSchema),
    values: {
      role: nurse.role,
      grade: nurse.grade,
      experience: nurse.experience,
      is_head_nurse: nurse.is_head_nurse,
      weekly_off_weekday: nurse.weekly_off_weekday,
      is_night_nurse: nurse.is_night_nurse ?? [],
      preceptor_id: nurse.preceptor_id,
      joining_date: filterDate(nurse.joining_date),
      resignation_date: filterDate(nurse.resignation_date),
      nurse_memo: nurse.nurse_memo,
      is_weekend_off: nurse.is_weekend_off ?? false,
      fixed_shift: nurse.fixed_shift ?? null,
      enable_aide: nurse.enable_aide ?? false,
      enable_nurse_pair_preference: nurse.enable_nurse_pair_preference ?? false,
      wanted_max_requests: nurse.wanted_max_requests ?? null,
    },
  });
  useEffect(() => {
    if (isEditMode) {
      // 수정 모드로 들어갈 때 폼 데이터 재설정
      reset({
        role: nurse.role,
        grade: nurse.grade,
        experience: nurse.experience,
        is_head_nurse: nurse.is_head_nurse,
        weekly_off_weekday: nurse.weekly_off_weekday,
        is_night_nurse: nurse.is_night_nurse ?? [],
        preceptor_id: nurse.preceptor_id,
        joining_date: filterDate(nurse.joining_date),
        resignation_date: filterDate(nurse.resignation_date),
        nurse_memo: nurse.nurse_memo,
        is_weekend_off: nurse.is_weekend_off ?? false,
        fixed_shift: nurse.fixed_shift ?? null, // 이 값이 제대로 들어가는지 확인
        enable_aide: nurse.enable_aide ?? false,
        enable_nurse_pair_preference:
          nurse.enable_nurse_pair_preference ?? false,
        wanted_max_requests: nurse.wanted_max_requests ?? null,
      });
    }
  }, [isEditMode, nurse]);

  const {
    reset,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { isDirty },
  } = methods;

  const enableAide = watch("enable_aide");
  const watchedGrade = watch("grade");

  /** `grade_names` 기준 옵션 + 폼 값이 목록에 없을 때(레거시 등) 1행 보정 */
  const gradeSelectOptions = useMemo(() => {
    const src = gradeData?.gradeSelectOptions ?? [];
    const base = src.length > 0 ? [...src] : [];
    const g =
      watchedGrade != null && watchedGrade !== 0 ? Number(watchedGrade) : null;
    if (g != null && !base.some((o) => o.value === String(g))) {
      base.push({
        value: String(g),
        label: resolveGradeLabel(g, src, gradeData?.grade_names ?? undefined),
      });
    }
    return base;
  }, [gradeData?.gradeSelectOptions, gradeData?.grade_names, watchedGrade]);

  const { mutate: nurseMutation, isPending: isSavePending } =
    useNurseMutation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { mutate: deleteNurseMutation, isPending: isDeletePending } =
    useMutation({
      mutationFn: async (id: string) => {
        await httpClient.delete(`/nurses/${id}`);
      },
      onSuccess: () => {
        toast.success("삭제되었습니다.");
        setIsEditMode(false);
        queryClient.invalidateQueries({
          queryKey: [QUERY_BASE_KEY.NURSES, groupId],
        });
        // 사이드프로필 캐싱 무효화
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.detail(nurse.nurse_id),
          refetchType: "all",
        });
        setIsDeleteModalOpen(false);
      },
    });

  const isPending = isSavePending || isDeletePending;

  // nurse prop이 변경되면 폼 데이터를 최신화 (수정 모드가 아닐 때만)
  useEffect(() => {
    if (!isEditMode) {
      reset({
        role: nurse.role,
        grade: nurse.grade,
        experience: nurse.experience,
        is_head_nurse: nurse.is_head_nurse,
        weekly_off_weekday: nurse.weekly_off_weekday,
        is_night_nurse: nurse.is_night_nurse ?? [],
        preceptor_id: nurse.preceptor_id,
        joining_date: filterDate(nurse.joining_date),
        resignation_date: filterDate(nurse.resignation_date),
        nurse_memo: nurse.nurse_memo,
        is_weekend_off: nurse.is_weekend_off ?? false,
        enable_aide: nurse.enable_aide ?? false,
        enable_nurse_pair_preference:
          nurse.enable_nurse_pair_preference ?? false,
        wanted_max_requests: nurse.wanted_max_requests ?? null,
      });
    }
  }, [nurse, reset, isEditMode]);

  // 주말휴무가 "미적용"으로 변경되면 고정근무 제거
  const isWeekendOff = watch("is_weekend_off");
  useEffect(() => {
    if (isEditMode && isWeekendOff === false) {
      setValue("fixed_shift", null);
    }
  }, [isWeekendOff, isEditMode, setValue]);

  // 외부 클릭 시 취소 확인 핸들러
  const handleClickOutside = useCallback(
    (event: PointerEvent) => {
      if (
        !rowRef.current ||
        !isEditMode ||
        isPending ||
        isNightWeekendBlockedModalOpen
      )
        return;

      const target = event.target as Node | null;
      if (!target) return;

      // Radix UI 포털 내부 클릭이거나, DOM에서 제거된 경우는 외부 클릭으로 간주하지 않음
      if (
        target instanceof Element &&
        (target.closest("[data-radix-portal]") ||
          target.closest("[data-radix-popper-content-wrapper]") ||
          target.closest("[role='listbox']") ||
          target.closest("[role='option']") ||
          target.closest("[role='dialog']") ||
          !document.body.contains(target))
      ) {
        return;
      }

      // 현재 행 내부 클릭이면 무시
      if (rowRef.current.contains(target)) return;

      // 외부 클릭 시 취소 확인
      if (isDirty) {
        if (confirm("수정된 내용이 있습니다.\n취소하시겠습니까?")) {
          reset();
          setIsEditMode(false);
        }
      } else {
        // temp 간호사인 경우 외부 클릭 시 삭제하거나 그대로 둘지 결정 필요.
        // 현재 로직상으론 그냥 수정 모드만 꺼짐.
        setIsEditMode(false);
      }
    },
    [isEditMode, isDirty, isPending, reset, isNightWeekendBlockedModalOpen]
  );

  useEffect(() => {
    document.addEventListener("pointerdown", handleClickOutside, true);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside, true);
    };
  }, [handleClickOutside]);

  const onSubmit = (data: NurseBulkFormValues) => {
    if (!isDirty) {
      setIsEditMode(false);
      return;
    }

    if (!get) {
      toast.error("데이터를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    // 근무형태가 'N' 이고 주말 휴무가 '적용' 인 경우
    if (
      data.is_night_nurse.length === 1 &&
      data.is_night_nurse.includes("N") &&
      data.is_weekend_off
    ) {
      setIsNightWeekendBlockeModalOpen(true);
      return;
    }

    const joiningDate = createUtcDate(data.joining_date);
    const resignationDate = createUtcDate(data.resignation_date);

    const mergedData: NurseResponse = {
      ...pureNurse,
      ...data,
      role: data.role || "",
      is_night_nurse: sortShiftArray(data.is_night_nurse) as UnionShift[],
      joining_date: joiningDate,
      resignation_date: resignationDate,
      nurse_memo: data.nurse_memo,
    } as NurseResponse;

    const updatedList = get.map((n) =>
      n.nurse_id === nurse.nurse_id ? mergedData : n
    );

    // 그룹 관리자 또는 그룹 간호사가 아닐 때만 수간호사 최소 1명 검증
    if (!isAdmin && !isGroupNurse) {
      const hasHeadNurse = updatedList.some(
        (n) => n.active === 1 && n.is_head_nurse === true
      );

      if (!hasHeadNurse) {
        toast.error("수간호사는 최소 1명이어야 합니다.");
        return;
      }
    }

    nurseMutation(updatedList, {
      onSuccess: () => {
        setIsEditMode(false);
        queryClient.invalidateQueries({
          queryKey: [QUERY_BASE_KEY.NURSES, groupId],
        });
        // 사이드프로필 캐싱 무효화
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.detail(nurse.nurse_id),
          refetchType: "all",
        });
      },
    });
  };

  // 근무자 삭제
  const onDeleteHandler = useCallback(() => {
    if (nurse.nurse_id.startsWith("temp_")) {
      if (onCancelNewNurse) onCancelNewNurse();
      return;
    }
    setIsDeleteModalOpen(true);
  }, [nurse.nurse_id, onCancelNewNurse]);

  const onError = (errors: FieldErrors<NurseBulkFormValues>) => {
    const errorList = Object.values(errors);
    if (errorList.length > 0) {
      const firstError = errorList[0];
      if (firstError?.message) {
        toast.error(firstError.message);
      }
    }
  };

  // preceptees가 한명이라도 있으면 프리셉터
  const isPreceptor = (nurse.preceptees?.length ?? 0) > 0;

  const isDisabledNurse =
    !!nurse.is_head_nurse && nurse.role === "RN" && !!isAdmin;
  const isExternalNurseInCurrentGroup =
    !!selectedGroupId && nurse.group_id !== selectedGroupId;

  useEffect(() => {
    if (isExternalNurseInCurrentGroup && isEditMode) {
      setIsEditMode(false);
    }
  }, [isExternalNurseInCurrentGroup, isEditMode]);

  return (
    <FormProvider {...methods}>
      <TableRow
        // disabled={isDisabledNurse}
        key={idx}
        ref={rowRef}
        className={cn(
          "bg-white group transition-colors",
          isEditMode && "z-10 bg-blue-50/50 relative",
          activeForSideProfile ? "bg-blue-50 hover:bg-blue-100" : "",
          enableDragHandle && "cursor-grab active:cursor-grabbing",
          enableSelection && "cursor-pointer hover:bg-gray-50",
          enableSelection && isSelected && "!bg-[#F0F0F2]"
        )}
        style={isEditMode ? { position: "relative" } : undefined}
        data-nid={nurse.nurse_id}
        onClick={(e) => {
          // 체크박스 클릭은 이벤트 버블링으로 처리되므로 여기서는 체크박스가 아닐 때만 토글
          const target = e.target as HTMLElement;
          if (
            enableSelection &&
            onToggleSelection &&
            !isEditMode &&
            !target.closest('input[type="checkbox"]') // 체크박스 클릭이 아닐 때만
          ) {
            onToggleSelection(nurse.nurse_id);
          }
        }}
      >
        {enableDragHandle && (
          <TableCell className="text-center cursor-grab active:cursor-grabbing">
            <GripVertical className="w-4 h-4 text-gray-400 mx-auto" />
          </TableCell>
        )}
        {/* 체크박스 컬럼 추가 */}
        {enableSelection &&
          (!visibleColumns || visibleColumns.includes("checkbox")) && (
            <TableCell className="font-medium text-center">
              <div className="flex items-center justify-center gap-2">
                {enableSelection && (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center cursor-pointer hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSelection?.(nurse.nurse_id);
                    }}
                    disabled={isEditMode}
                    aria-label={isSelected ? "선택 해제" : "선택"}
                  >
                    <img
                      src={
                        isSelected
                          ? images.icon_checkbox_fill_gray
                          : images.icon_checkbox_gray
                      }
                      alt=""
                      aria-hidden="true"
                      className="h-[15px] w-[15px]"
                    />
                  </button>
                )}
              </div>
            </TableCell>
          )}
        {(!visibleColumns || visibleColumns.includes("No.")) && (
          <TableCell
            className={cn(
              "font-medium text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {idx + 1}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("사번")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {nurse.nurse_id || "-"}
          </TableCell>
        )}
        {/* 이름 */}
        {(!visibleColumns || visibleColumns.includes("이름")) && (
          <TableCell
            className={cn(
              "cursor-pointer underline-offset-4 decoration-1 hover:underline transition-all text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
            onClick={() => {
              if (selectIdForSideprofile) {
                selectIdForSideprofile(activeForSideProfile, nurse.nurse_id);
              }
            }}
          >
            {nurse.name}
          </TableCell>
        )}
        {/* role */}
        {(!visibleColumns || visibleColumns.includes("직종")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="role"
                render={({ field }) => (
                  <Select
                    value={field.value ?? ""}
                    onValueChange={field.onChange}
                    disabled={isPending}
                  >
                    <SelectTrigger className="h-[30px]! w-[80px] bg-white text-center">
                      <SelectValue placeholder="직종" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RN">RN</SelectItem>
                      <SelectItem value="AN">AN</SelectItem>
                      <SelectItem value="etc">etc</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            ) : nurse.role ? (
              <span className="inline-block max-w-[72px] truncate align-middle">
                {nurse.role}
              </span>
            ) : (
              <span className="opacity-40">-</span>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("직책")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {(nurse as NurseWithRelation & { _level?: string | null }).level_ ||
            (nurse as NurseWithRelation & { _level?: string | null })._level ? (
              <span className="inline-block max-w-[72px] truncate align-middle">
                {(nurse as NurseWithRelation & { _level?: string | null })
                  .level_ ??
                  (nurse as NurseWithRelation & { _level?: string | null })
                    ._level}
              </span>
            ) : (
              <span className="opacity-40">-</span>
            )}
          </TableCell>
        )}
        {visibleColumns?.includes("성별") && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {nurse.gender || "-"}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("나이(만)")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {nurse.age ? (
              `${nurse.age}세`
            ) : (
              <span className="opacity-40">-</span>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("Grade")) && (
          <TableCell
            className={cn(
              "text-center flex items-center justify-center mt-1",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="grade"
                render={({ field }) => (
                  <Select
                    value={
                      field.value != null && field.value !== 0
                        ? String(field.value)
                        : "none"
                    }
                    onValueChange={(value) =>
                      field.onChange(value === "none" ? 0 : Number(value))
                    }
                    disabled={isPending}
                  >
                    <SelectTrigger className=" h-[30px]! min-w-[50px] px-2 bg-white">
                      <SelectValue placeholder="-" />
                    </SelectTrigger>
                    <SelectContent className="min-w-[60px] max-h-[200px]">
                      <SelectItem value="none">-</SelectItem>
                      {gradeSelectOptions.map((o) => (
                        <SelectItem
                          key={o.value}
                          value={o.value}
                          // className="max-w-[72px]! overflow-hidden!"
                        >
                          <span className="truncate max-w-[72px]! overflow-hidden!">
                            {o.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            ) : nurse.grade && nurse.grade !== 0 ? (
              <span className="truncate max-w-[72px]! overflow-hidden!  inline-block">
                {resolveGradeLabel(
                  nurse.grade,
                  gradeData?.gradeSelectOptions,
                  gradeData?.grade_names ?? undefined
                )}
              </span>
            ) : (
              <span className="opacity-40">-</span>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("경력")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <div className="flex items-center justify-center">
                <Controller
                  control={control}
                  name="experience"
                  render={({ field }) => (
                    <Input
                      type="number"
                      min="0"
                      disabled={isPending}
                      className="w-[50px] h-[30px]! px-2 text-center bg-white"
                      value={field.value ?? 0}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val === "" ? 0 : Number(val));
                      }}
                    />
                  )}
                />
                <span className="ml-1 text-xs!">년</span>
              </div>
            ) : (
              <>
                {nurse.experience}
                <span className="text-sm">년</span>
              </>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("관리자")) && (
          <TableCell className="text-center">
            <div className="flex justify-center items-center">
              <Controller
                control={control}
                name="is_head_nurse"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={!isEditMode || isPending}
                  />
                )}
              />
            </div>
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("프리셉터")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isPreceptor ? (
              "프리셉터"
            ) : nurse.preceptor?.name ? (
              <span>{nurse.preceptor.name}</span>
            ) : (
              <span className="opacity-40">없음</span>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("주휴")) && (
          <TableCell
            className={cn(
              "text-center overflow-visible",
              isEditMode ? "relative z-10" : "",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="weekly_off_weekday"
                render={({ field }) => (
                  <Select
                    value={field.value != null ? String(field.value) : "none"}
                    onValueChange={(value) =>
                      field.onChange(value === "none" ? null : Number(value))
                    }
                    disabled={isPending}
                  >
                    <SelectTrigger className="h-[30px]!  px-2 bg-white">
                      <SelectValue placeholder="-" />
                    </SelectTrigger>
                    <SelectContent className="min-w-[60px] max-h-[200px]">
                      <SelectItem disabled={isPending} value="none">
                        -
                      </SelectItem>
                      <SelectItem disabled={isPending} value="0">
                        월
                      </SelectItem>
                      <SelectItem disabled={isPending} value="1">
                        화
                      </SelectItem>
                      <SelectItem disabled={isPending} value="2">
                        수
                      </SelectItem>
                      <SelectItem disabled={isPending} value="3">
                        목
                      </SelectItem>
                      <SelectItem disabled={isPending} value="4">
                        금
                      </SelectItem>
                      <SelectItem disabled={isPending} value="5">
                        토
                      </SelectItem>
                      <SelectItem disabled={isPending} value="6">
                        일
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            ) : (
              <div className="text-center">
                {nurse.weekly_off_weekday != null ? (
                  getWeekdayLabel(nurse.weekly_off_weekday as Weekday)
                ) : (
                  <span className="opacity-40">-</span>
                )}
              </div>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("근무형태")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            <div className="w-full flex justify-center items-center">
              {isEditMode ? (
                <ShiftPopover
                  shiftOptions={shiftOptions}
                  fixedShiftOptions={fixedShiftOptions}
                  disabled={isPending}
                />
              ) : (
                <div className="flex justify-center items-center gap-0.5">
                  {nurse.fixed_shift ? (
                    // 고정근무가 있으면 "{name} 고정" 형태로 표시
                    <span
                      className={cn(
                        "text-sm font-medium  text-white p-1.5 px-2 rounded-sm"
                      )}
                      style={{
                        backgroundColor: shiftMapper?.getColor(
                          nurse.fixed_shift
                        ),
                      }}
                    >
                      <span className="font-bold">
                        {fixedShiftOptions.find(
                          (o) => o.value === nurse.fixed_shift
                        )?.value ?? nurse.fixed_shift}
                      </span>
                      <span className="ml-1">고정</span>
                    </span>
                  ) : (
                    // is_night_nurse 배열의 값들을 그대로 표시
                    // 빈 배열일 때는 isUseMid에 따라 M 포함 여부 결정
                    (() => {
                      const nightNurseShifts = nurse.is_night_nurse || [];

                      // 빈 배열이면 모든 shiftOptions 표시 (isUseMid에 따라 M 포함 여부 결정)
                      if (nightNurseShifts.length === 0) {
                        const allShifts = shiftOptions.filter((opt) => {
                          // isUseMid가 false면 M 제외
                          if (isUseMid === false && opt.value === "M") {
                            return false;
                          }
                          return true;
                        });

                        if (allShifts.length === 0) {
                          return <span className="text-sm opacity-40">-</span>;
                        }

                        return allShifts.map((opt, i) => {
                          const color =
                            defaultShiftColorMap.get(opt.value) ||
                            shiftMapper?.getColor(opt.value) ||
                            "#000000";
                          return (
                            <span
                              key={i}
                              className="font-bold size-6.5 text-white flex items-center justify-center shrink-0 rounded-[5px] text-xs"
                              style={{
                                backgroundColor: color,
                              }}
                            >
                              {opt.label}
                            </span>
                          );
                        });
                      }

                      // 배열에 값이 있으면 해당 값들을 그대로 표시
                      const shiftsToDisplay = nightNurseShifts
                        .map((shiftCode) => {
                          // shiftOptions에서 해당 코드 찾기
                          const opt = shiftOptions.find(
                            (o) => o.value === shiftCode
                          );
                          if (!opt) return null;
                          // isUseMid가 false면 M 제외
                          if (isUseMid === false && shiftCode === "M") {
                            return null;
                          }
                          return opt;
                        })
                        .filter(
                          (opt): opt is { label: string; value: string } =>
                            opt !== null
                        );

                      if (shiftsToDisplay.length === 0) {
                        return <span className="text-sm opacity-40">-</span>;
                      }

                      return shiftsToDisplay.map((opt, i) => {
                        const color =
                          defaultShiftColorMap.get(opt.value) ||
                          shiftMapper?.getColor(opt.value) ||
                          "#000000";
                        return (
                          <span
                            key={i}
                            className="font-bold size-6.5 text-white flex items-center justify-center shrink-0 rounded-[5px] text-xs"
                            style={{
                              backgroundColor: color,
                            }}
                          >
                            {opt.label}
                          </span>
                        );
                      });
                    })()
                  )}
                </div>
              )}
            </div>
          </TableCell>
        )}

        {(!visibleColumns || visibleColumns.includes("주말 휴무")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="is_weekend_off"
                render={({ field }) => (
                  <Select
                    value={field.value ? "true" : "false"}
                    onValueChange={(val) => field.onChange(val === "true")}
                    disabled={isPending}
                  >
                    <SelectTrigger className="h-[30px]! w-[80px] px-2 bg-white mx-auto">
                      <SelectValue placeholder="적용" />
                    </SelectTrigger>
                    <SelectContent className="min-w-[60px]">
                      <SelectItem value="true">적용</SelectItem>
                      <SelectItem value="false">미적용</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            ) : (
              <div className="text-center">
                {nurse.is_weekend_off ? (
                  "적용"
                ) : (
                  <span className="text-sm opacity-40">미적용</span>
                )}
              </div>
            )}
          </TableCell>
        )}
        {/* {(!visibleColumns || visibleColumns.includes("원티드 요청 제한")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <div className="flex items-center justify-center">
                <Controller
                  control={control}
                  name="wanted_max_requests"
                  render={({ field }) => (
                    <Input
                      type="number"
                      min="0"
                      disabled={isPending}
                      className="w-[50px] h-[30px]! px-2 text-center bg-white"
                      value={field.value ?? ""}
                      placeholder="-"
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val === "" ? null : Number(val));
                      }}
                    />
                  )}
                />
              </div>
            ) : (
              <>{nurse.wanted_max_requests ?? "-"}</>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("원티드 AI기능 사용")) && (
          <TableCell className="text-center">
            <div className="flex justify-center items-center">
              <Controller
                control={control}
                name="enable_aide"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      if (!checked) {
                        setValue("enable_nurse_pair_preference", false, {
                          shouldDirty: true,
                        });
                      }
                    }}
                    disabled={!isEditMode || isPending}
                  />
                )}
              />
            </div>
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("시크릿기능 사용")) && (
          <TableCell className="text-center">
            <div className="flex justify-center items-center">
              <Controller
                control={control}
                name="enable_nurse_pair_preference"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={!isEditMode || isPending || !enableAide}
                  />
                )}
              />
            </div>
          </TableCell>
        )} */}
        {(!visibleColumns || visibleColumns.includes("입사일")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="joining_date"
                render={({ field }) => (
                  <StyledDateInput
                    value={field.value ?? null}
                    onChange={field.onChange}
                    placeholder="-"
                    disabled={isPending}
                  />
                )}
              />
            ) : (
              <>
                {nurse.joining_date ? (
                  DateUtils.dateFormatKR(nurse.joining_date, "YYYY-MM-DD")
                ) : (
                  <span className="opacity-40">-</span>
                )}
              </>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("재직기간")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {DateUtils.getTenureString(
              nurse.joining_date,
              nurse.resignation_date
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("근무 종료일")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="resignation_date"
                render={({ field }) => (
                  <StyledDateInput
                    value={field.value ?? null}
                    onChange={field.onChange}
                    placeholder="-"
                    min={watch("joining_date") || undefined}
                    disabled={!watch("joining_date") || isPending}
                  />
                )}
              />
            ) : (
              <>
                {nurse.resignation_date ? (
                  DateUtils.dateFormatKR(nurse.resignation_date, "YYYY-MM-DD")
                ) : (
                  <span className="opacity-40">-</span>
                )}
              </>
            )}
          </TableCell>
        )}
        {visibleColumns?.includes("생년월일") && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {formattedBirthDate}
          </TableCell>
        )}
        {visibleColumns?.includes("연락처") && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {nurse.phone_number || "-"}
          </TableCell>
        )}
        {visibleColumns?.includes("소속") && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {(nurse as NurseWithRelation & { affiliation?: string })
              .affiliation || "-"}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("메모")) && (
          <TableCell
            className={cn(isSelected && enableSelection && "text-[#D6D6DE]")}
          >
            <Controller
              control={control}
              name="nurse_memo"
              render={({ field }) => (
                <MemoCell
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  isEditMode={isEditMode}
                  disabled={isPending}
                  onlyView={isSelected && enableSelection}
                />
              )}
            />
          </TableCell>
        )}
        {!hideEditColumn && (
          <TableCell className="text-center">
            <div className="w-full flex justify-center items-center">
              {isEditMode ? (
                <button
                  type="button"
                  className={`p-1 rounded hover:bg-blue-100 transition-colors ${isPending ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={handleSubmit(onSubmit, onError)}
                  disabled={isPending}
                  title="저장"
                >
                  저장
                </button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <PencilLine
                          className={`size-4 transition-colors ${disableEdit || isDisabledNurse || isExternalNurseInCurrentGroup ? "text-gray-300 cursor-not-allowed" : "text-[#D6D6DE] cursor-pointer hover:text-[#0E59C6]"}`}
                          onClick={() =>
                            !disableEdit &&
                            !isDisabledNurse &&
                            !isExternalNurseInCurrentGroup &&
                            setIsEditMode(true)
                          }
                        />
                      </div>
                    </TooltipTrigger>
                    {(disableEdit ||
                      isDisabledNurse ||
                      isExternalNurseInCurrentGroup) && (
                      <TooltipContent>
                        <p>
                          {disableEdit
                            ? "정렬 모드에서는 수정이 불가합니다"
                            : isExternalNurseInCurrentGroup
                              ? "파견 근무자는 사이드프로필에서만 수정이 가능합니다"
                              : "파트장 정보는 수정할 수 없습니다"}
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </TableCell>
        )}

        {isEditMode && (
          <td className="absolute -right-[10px] top-1/2 -translate-y-1/2 translate-x-full p-0 border-0">
            {!isDisabledNurse && (
              <button
                type="button"
                className="p-2 rounded-full border transition-colors"
                style={{
                  backgroundColor: isHovered ? "#D6D6DE" : "#fafafa",
                  borderColor: "#D6D6DE",
                }}
                onClick={onDeleteHandler}
                disabled={isPending}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                title="삭제"
              >
                <Trash2
                  size={18}
                  style={{
                    color: isHovered ? "#fafafa" : "#D6D6DE",
                  }}
                  className="transition-colors"
                />
              </button>
            )}
          </td>
        )}
      </TableRow>
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          memberName={nurse.name}
          groupName={groupName}
          onConfirm={() => {
            deleteNurseMutation(nurse.nurse_id);
          }}
          onCancel={() => setIsDeleteModalOpen(false)}
        />
      )}

      <CustomModal.Alert
        isOpen={isNightWeekendBlockedModalOpen}
        onClose={() => setIsNightWeekendBlockeModalOpen(false)}
        title="나이트 전담은 주말 휴무를 적용할 수 없습니다."
      />
    </FormProvider>
  );
}
